import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

// Components
import DiscountCoupons from "../discountCoupon";
import {
  AnimatedModal,
  ModalContent,
  ModalHeader,
} from "../modals/AnimatedModal";

// Material UI
import Box from "@mui/material/Box";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import Skeleton from "@mui/material/Skeleton";
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import { alpha, useTheme } from "@mui/material/styles";
import Chip from "@mui/material/Chip";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// Icons
import DiscountIcon from "@mui/icons-material/Discount";
import PercentIcon from "@mui/icons-material/Percent";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DoDisturbOnIcon from "@mui/icons-material/DoDisturbOn";

// Services
import {
  getDeliveryCenterConfig,
  setDeliveryCenterConfig,
} from "../../api/DeliveryCenter/deliveryCenter";
import { dispatchSnackBarMessage } from "../../utils/snackbar";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

// Enhanced switch component with animation
const AnimatedSwitch = ({
  checked,
  onChange,
  label,
  icon: Icon,
  description,
}) => {
  const theme = useTheme();

  return (
    <motion.div
      variants={itemVariants}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <Card
        elevation={1}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          transition: "all 0.3s ease",
          height: "100%",
          position: "relative",
          "&:hover": {
            boxShadow: theme.shadows[3],
          },
          borderLeft: `4px solid ${
            checked ? theme.palette.primary.main : theme.palette.grey[300]
          }`,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  backgroundColor: alpha(
                    checked
                      ? theme.palette.primary.main
                      : theme.palette.grey[400],
                    0.1
                  ),
                  borderRadius: "50%",
                  p: 1,
                  display: "flex",
                  mr: 2,
                  color: checked
                    ? theme.palette.primary.main
                    : theme.palette.grey[500],
                }}
              >
                <Icon />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  {label}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mt: 0.5 }}
                >
                  {description}
                </Typography>
              </Box>
            </Box>

            <Switch
              checked={checked}
              onChange={onChange}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: theme.palette.primary.main,
                },
              }}
            />
          </Box>

          <Chip
            icon={
              checked ? (
                <CheckCircleIcon fontSize="small" />
              ) : (
                <DoDisturbOnIcon fontSize="small" />
              )
            }
            label={checked ? "Ativado" : "Desativado"}
            size="small"
            color={checked ? "success" : "default"}
            variant={checked ? "filled" : "outlined"}
            sx={{
              mt: 1.5,
              height: 24,
              fontSize: "0.75rem",
              fontWeight: "medium",
            }}
          />
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default function DiscountCouponsModal({ show, closeModal }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [configs, setConfigs] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (show) {
      loadConfig();
    }
  }, [show]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const params = {
        config:
          "allow_coupons_discounts,allow_percentage_discount,allow_money_discount",
      };
      const { data } = await getDeliveryCenterConfig(params);
      setConfigs(data);
    } catch (err) {
      console.log(err);
      setErrorMessage("Erro ao carregar configurações de cupons de desconto");
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAllowCouponsSwitch = async (event) => {
    try {
      const newCheckedValue = event.target.checked;
      const data = {
        config: {
          allow_coupons_discounts: newCheckedValue,
        },
      };
      await setDeliveryCenterConfig(data);
      setConfigs({ ...configs, allow_coupons_discounts: newCheckedValue });

      dispatchSnackBarMessage(
        `allow-coupons-switch-success-${new Date()}`,
        `Descontos por cupons ${
          newCheckedValue ? "ativados" : "desativados"
        } com sucesso`,
        "success"
      );
    } catch (e) {
      dispatchSnackBarMessage(
        `allow-coupons-switch-error-${new Date()}`,
        "Erro ao mudar status dos cupons de desconto",
        "error"
      );
    }
  };

  const handleAllowPercentageDiscount = async (event) => {
    try {
      const newCheckedValue = event.target.checked;
      const data = {
        config: {
          allow_percentage_discount: newCheckedValue,
        },
      };
      await setDeliveryCenterConfig(data);
      setConfigs({ ...configs, allow_percentage_discount: newCheckedValue });

      dispatchSnackBarMessage(
        `allow-percentage-switch-success-${new Date()}`,
        `Descontos por porcentagem ${
          newCheckedValue ? "ativados" : "desativados"
        } com sucesso`,
        "success"
      );
    } catch (e) {
      dispatchSnackBarMessage(
        `allow-percentage-switch-error-${new Date()}`,
        "Erro ao mudar status de descontos por porcentagem",
        "error"
      );
    }
  };

  const handleAllowMoneyDiscount = async (event) => {
    try {
      const newCheckedValue = event.target.checked;
      const data = {
        config: {
          allow_money_discount: newCheckedValue,
        },
      };
      await setDeliveryCenterConfig(data);
      setConfigs({ ...configs, allow_money_discount: newCheckedValue });

      dispatchSnackBarMessage(
        `allow-money-switch-success-${new Date()}`,
        `Descontos por valor em Real ${
          newCheckedValue ? "ativados" : "desativados"
        } com sucesso`,
        "success"
      );
    } catch (e) {
      dispatchSnackBarMessage(
        `allow-money-switch-error-${new Date()}`,
        "Erro ao mudar status de descontos por valor em Real",
        "error"
      );
    }
  };

  return (
    <AnimatedModal
      isOpen={show}
      onClose={closeModal}
      width="80%"
      maxWidth="1200px"
    >
      <ModalHeader
        title="Cupons de Desconto"
        icon={DiscountIcon}
        onClose={closeModal}
      />
      <ModalContent>
        <Box sx={{ px: 3, pt: 2 }}>
          <Collapse in={showError} sx={{ mb: 2 }}>
            <Alert
              severity="error"
              onClose={() => setShowError(false)}
              sx={{ mb: 2, borderRadius: 2 }}
            >
              {errorMessage}
            </Alert>
          </Collapse>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 3,
              background: alpha(theme.palette.primary.light, 0.07),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <InfoOutlinedIcon
                sx={{ mr: 1, color: theme.palette.primary.main }}
              />
              <Typography
                variant="subtitle1"
                fontWeight="medium"
                color="primary"
              >
                Configurações de Descontos
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary">
              Configure quais tipos de descontos serão permitidos na sua
              operação. As alterações serão aplicadas imediatamente.
            </Typography>
          </Paper>

          {loading ? (
            <Box sx={{ mt: 2, mb: 3 }}>
              <Grid container spacing={2}>
                {[1, 2, 3].map((item) => (
                  <Grid item xs={12} md={4} key={item}>
                    <Skeleton
                      variant="rectangular"
                      height={120}
                      sx={{ borderRadius: 3 }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            <Box sx={{ mt: 2, mb: 3 }}>
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <AnimatedSwitch
                      checked={configs?.allow_coupons_discounts || false}
                      onChange={handleAllowCouponsSwitch}
                      label="Descontos por Cupons"
                      icon={LocalOfferIcon}
                      description="Permite aplicar descontos usando códigos promocionais"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <AnimatedSwitch
                      checked={configs?.allow_percentage_discount || false}
                      onChange={handleAllowPercentageDiscount}
                      label="Descontos por Porcentagem"
                      icon={PercentIcon}
                      description="Permite aplicar descontos percentuais nos pedidos"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <AnimatedSwitch
                      checked={configs?.allow_money_discount || false}
                      onChange={handleAllowMoneyDiscount}
                      label="Descontos por Valor"
                      icon={MonetizationOnIcon}
                      description="Permite aplicar descontos em valor monetário fixo"
                    />
                  </Grid>
                </Grid>
              </motion.div>
            </Box>
          )}

          <Divider
            sx={{
              my: 3,
              "&::before, &::after": {
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              },
            }}
          >
            <Chip
              label="Gerenciar Cupons"
              color="primary"
              size="small"
              icon={<DiscountIcon fontSize="small" />}
            />
          </Divider>
        </Box>

        <DiscountCoupons />
      </ModalContent>
    </AnimatedModal>
  );
}
